import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiSelectableChipsComponent } from '@/ui/selectable-chips/ui-selectable-chips';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { arraysContainSameElements } from '@/utils/toolkit/array/arrays-contain-same-elements.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceMeasure,
    BusinessIntelligenceTemporalDimensionStep
} from '../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligencePivotTableRepresentationConfig } from '../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import type { BusinessIntelligenceDimensionConfiguration } from '../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../../providers/business-intelligence/business-intelligence.provider';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';

const maxColumns = 42;
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-pivot-table-representation-config-editor.scss'],
    selector: 'business-intelligence-pivot-table-representation-config-editor',
    templateUrl: 'business-intelligence-pivot-table-representation-config-editor.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiFormFieldComponent,
        UiSelectableChipsComponent,
        UiSelectComponent,
        AsyncPipe,
        TranslatePipe
    ]
})
export class BusinessIntelligencePivotTableRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligencePivotTableRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);
    private _translateService = inject(TranslateService);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligencePivotTableRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligencePivotTableRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    stepList = Object.values(BusinessIntelligenceTemporalDimensionStep).map(step => {
        return {
            value: step,
            label: this._translateService.instant('STEPS.' + step.toUpperCase())
        };
    });

    lineAvailableDimensions$!: Observable<BusinessIntelligenceDimension[]>;
    pivotAvailableDimensions$!: Observable<BusinessIntelligenceDimension[]>;

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligencePivotTableRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;

    isPivotTemporalDimension$!: Observable<boolean>;

    ngOnInit() {
        const dimensions$ = this.availableDimensions$.pipe(filter(isNotNullish), distinctUntilChanged(arraysContainSameElements));
        this.lineAvailableDimensions$ = dimensions$.pipe(
            map(dimensions => {
                return dimensions.filter(dimension => !this._businessIntelligenceProvider.isTemporalDimension(dimension));
            })
        );

        this.formGroup = this._fb.group({
            linesDimensions: [null, [Validators.required, Validators.maxLength(maxColumns)]],
            pivotDimensionCode: [null, [Validators.required]],
            pivotStepCode: [null],
            measureCode: [null],
            sortMemberCode: null,
            isSortReversed: false
        });
        this.pivotAvailableDimensions$ = observeControlValue$(this.formGroup.controls.linesDimensions).pipe(
            withLatestFrom(dimensions$),
            map(([linesDimensions, dimensions]) => {
                const setToRemove = new Set(linesDimensions);
                return dimensions.filter(element => !setToRemove.has(element));
            })
        );

        const pivotDimension$ = combineLatest([
            this.pivotAvailableDimensions$,
            observeControlValue$(this.formGroup.controls.pivotDimensionCode)
        ]).pipe(map(([dimensionList, dimensionCode]) => dimensionList.find(dimension => dimension.dimensionCode === dimensionCode)));

        this.isPivotTemporalDimension$ = pivotDimension$.pipe(
            map(dimension => this._businessIntelligenceProvider.isTemporalDimension(dimension))
        );

        this.config$.pipe(withLatestFrom(dimensions$), untilDestroyed(this)).subscribe(([config, dimensions]) => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        linesDimensions: [],
                        pivotDimensionCode: null,
                        pivotStepCode: null,
                        measureCode: null,
                        sortMemberCode: null,
                        isSortReversed: false
                    },
                    { emitEvent: false }
                );
            } else {
                const lines = config.lines
                    .map(element => {
                        const dimension = dimensions.find(dimension => dimension.dimensionCode === element.dimensionCode);
                        return dimension ? dimension : null;
                    })
                    .filter(isNotNullish);

                this.formGroup.setValue(
                    {
                        linesDimensions: lines,
                        pivotDimensionCode: config.pivot ? config.pivot.dimensionCode : null,
                        pivotStepCode: config.pivot ? (config.pivot.groupingParams ? config.pivot.groupingParams.granularity : null) : null,
                        measureCode: config.measure ? config.measure.measureCode : null,
                        sortMemberCode: null,
                        isSortReversed: false
                    },
                    { emitEvent: false }
                );
            }
        });
        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(formValue => {
            this.configChanged.emit(this.generateConfigFromForm(formValue));
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    private generateConfigFromForm(formValue: any): BusinessIntelligencePivotTableRepresentationConfig {
        const pivotDimension = this.availableDimensions.find(dim => dim.dimensionCode === formValue.pivotDimensionCode);

        const pivotDimensionConfiguration: BusinessIntelligenceDimensionConfiguration = formValue.pivotDimensionCode
            ? {
                  dimensionCode: formValue.pivotDimensionCode ? formValue.pivotDimensionCode : null,
                  groupingParams:
                      !formValue.pivotStepCode || !this._businessIntelligenceProvider.isTemporalDimension(pivotDimension)
                          ? null
                          : {
                                granularity: formValue.pivotStepCode
                            }
              }
            : null!;

        return {
            lines: formValue.linesDimensions.map((line: any) => {
                return { dimensionCode: line.dimensionCode };
            }),
            pivot: pivotDimensionConfiguration,
            measure: { measureCode: formValue.measureCode ? formValue.measureCode : null },
            sortCriteria: null as unknown as any[]
        };
    }
}
